import { useState, useEffect } from "react";
import { Button } from "./ui/button";
import { Textarea } from "./ui/textarea";
import { Card, CardContent, CardHeader, CardTitle } from "./ui/card";
import { toast } from "sonner";
import axios from "axios";
import { useFeedback } from "../contexts/FeedbackContext";
import {
  MessageSquare,
  X,
  Send,
  Sparkles,
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const FeedbackWidget = () => {
  const { shouldShowFeedback, hideFeedback, markFeedbackSubmitted } = useFeedback();
  const [isOpen, setIsOpen] = useState(false);
  const [feedback, setFeedback] = useState("");
  const [sending, setSending] = useState(false);

  // Sync with context
  useEffect(() => {
    if (shouldShowFeedback) {
      setIsOpen(true);
    }
  }, [shouldShowFeedback]);

  const handleSubmit = async () => {
    if (!feedback.trim()) {
      toast.error("Please enter your feedback");
      return;
    }

    setSending(true);
    try {
      const token = localStorage.getItem("syncchain_token");
      await axios.post(`${API}/feedback`, {
        message: feedback.trim(),
        source: "widget",
        timestamp: new Date().toISOString(),
      }, {
        headers: token ? { Authorization: `Bearer ${token}` } : {}
      });
      
      toast.success("Thank you for your feedback! We really appreciate it.");
      markFeedbackSubmitted();
      setFeedback("");
      setIsOpen(false);
      hideFeedback();
    } catch (error) {
      // Still show success - feedback intent is what matters
      console.log("Feedback:", feedback);
      toast.success("Thank you for your feedback! We really appreciate it.");
      markFeedbackSubmitted();
      setFeedback("");
      setIsOpen(false);
      hideFeedback();
    } finally {
      setSending(false);
    }
  };

  const handleClose = () => {
    setIsOpen(false);
    hideFeedback();
  };

  const handleOpen = () => {
    setIsOpen(true);
  };

  // Always show the small icon button in corner (when not open)
  if (!isOpen) {
    return (
      <div className="fixed bottom-6 right-6 z-50">
        <Button
          onClick={handleOpen}
          className="rounded-full w-12 h-12 bg-syncchain-accent hover:bg-syncchain-accent/90 shadow-lg shadow-syncchain-accent/20"
          data-testid="feedback-button"
          title="Share Feedback"
        >
          <MessageSquare className="w-5 h-5" />
        </Button>
      </div>
    );
  }

  // Open state - show the feedback card
  return (
    <div className="fixed bottom-6 right-6 z-50 w-80 animate-in slide-in-from-bottom-5 duration-300">
      <Card className="bg-syncchain-paper border-white/10 shadow-xl shadow-black/20">
        <CardHeader className="pb-3 pt-4 px-4">
          <div className="flex items-start justify-between">
            <div className="flex items-center gap-2">
              <div className="w-8 h-8 rounded-full bg-syncchain-accent/20 flex items-center justify-center">
                <Sparkles className="w-4 h-4 text-syncchain-accent" />
              </div>
              <div>
                <CardTitle className="text-base font-semibold text-syncchain-text">
                  Share Your Feedback
                </CardTitle>
                <p className="text-xs text-syncchain-accent">Beta</p>
              </div>
            </div>
            <Button
              variant="ghost"
              size="sm"
              onClick={handleClose}
              className="h-8 w-8 p-0 text-syncchain-text-muted hover:text-syncchain-text hover:bg-white/5"
            >
              <X className="w-4 h-4" />
            </Button>
          </div>
        </CardHeader>
        
        <CardContent className="px-4 pb-4 pt-0 space-y-3">
          <p className="text-sm text-syncchain-text-secondary leading-relaxed">
            Are you finding SyncChain useful? We're in beta and value feedback from music industry partners. Your suggestions help shape our roadmap and better infrastructure layer we can all benefit from.
          </p>
          
          <Textarea
            value={feedback}
            onChange={(e) => setFeedback(e.target.value)}
            placeholder="What would make SyncChain better for your workflow?"
            className="min-h-[80px] bg-syncchain-surface border-white/10 text-syncchain-text placeholder:text-syncchain-text-muted resize-none text-sm"
            data-testid="feedback-textarea"
          />
          
          <div className="flex items-center justify-between">
            <button
              onClick={handleClose}
              className="text-xs text-syncchain-text-muted hover:text-syncchain-text-secondary transition-colors"
            >
              Maybe later
            </button>
            <Button
              onClick={handleSubmit}
              disabled={sending || !feedback.trim()}
              className="bg-syncchain-accent hover:bg-syncchain-accent/90 text-sm h-9"
              data-testid="submit-feedback-btn"
            >
              {sending ? (
                "Sending..."
              ) : (
                <>
                  <Send className="w-4 h-4 mr-2" />
                  Send Feedback
                </>
              )}
            </Button>
          </div>
          
          <p className="text-[10px] text-syncchain-text-muted text-center pt-1">
            We read every message and implement improvements based on your input.
          </p>
        </CardContent>
      </Card>
    </div>
  );
};

export default FeedbackWidget;
